/**
 * MarkdownEditor — lightweight WYSIWYG-ish authoring surface for Mission /
 * Strategy / Constitution change-summaries.
 *
 * Per ADR-055 spirit ("no SDK lock-in / no heavy editor package"), this
 * editor avoids dragging TipTap / Lexical into the bundle. The implementation:
 *
 *   - A `<textarea>` is the source of truth (raw Markdown).
 *   - A toolbar applies common transforms (bold, italic, headings, lists,
 *     blockquote, code, link) by mutating the textarea selection. The
 *     selection model is the browser's standard one — no virtual DOM
 *     intermediate.
 *   - A side-by-side preview pane uses the pre-existing `react-markdown`
 *     dep (already shipped in chaos-ui) with `remark-gfm` for tables / task
 *     lists and `rehype-highlight` for fenced code blocks. Both deps are
 *     already in chaos-ui/package.json.
 *
 * The exported `MarkdownEditor` accepts a controlled value + onChange so it
 * plays nicely with react-hook-form. Keyboard shortcuts (Cmd/Ctrl+B/I) are
 * wired through the same transforms the toolbar uses, so the two paths stay
 * in sync.
 */
import {
  Bold,
  Code,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Pencil,
  Quote,
} from 'lucide-react';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';

import { cn } from '../../lib/utils';

interface MarkdownEditorProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** When set, locks the editor (read-only preview only). */
  disabled?: boolean;
  /** Rows passed to the underlying textarea. */
  rows?: number;
  /** Set ID for label association. */
  id?: string;
  /** ARIA-described-by id. */
  describedBy?: string;
  className?: string;
}

type ToolbarMode = 'edit' | 'preview';

/**
 * Apply a Markdown transform to the current textarea selection.
 *
 * `pattern` is split into `before` / `after`; when `after` is null we treat
 * the pattern as a line-prefix transform (headings, lists, blockquote).
 */
function applyInline(
  el: HTMLTextAreaElement,
  before: string,
  after: string,
): { value: string; selection: { start: number; end: number } } {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const selected = el.value.slice(start, end);
  const next = `${el.value.slice(0, start)}${before}${selected}${after}${el.value.slice(end)}`;
  const cursorStart = start + before.length;
  const cursorEnd = cursorStart + selected.length;
  return { value: next, selection: { start: cursorStart, end: cursorEnd } };
}

function applyLinePrefix(
  el: HTMLTextAreaElement,
  prefix: string,
): { value: string; selection: { start: number; end: number } } {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  // Extend selection to whole lines so the prefix applies once per line.
  const lineStart = el.value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = el.value.indexOf('\n', end);
  const tail = lineEnd === -1 ? el.value.length : lineEnd;
  const block = el.value.slice(lineStart, tail);
  const transformed = block
    .split('\n')
    .map((line) => (line.startsWith(prefix) ? line : `${prefix}${line}`))
    .join('\n');
  const next = `${el.value.slice(0, lineStart)}${transformed}${el.value.slice(tail)}`;
  return {
    value: next,
    selection: {
      start: lineStart,
      end: lineStart + transformed.length,
    },
  };
}

function applyLink(el: HTMLTextAreaElement): {
  value: string;
  selection: { start: number; end: number };
} {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const selected = el.value.slice(start, end) || 'link text';
  const placeholderUrl = 'https://';
  const replacement = `[${selected}](${placeholderUrl})`;
  const next = `${el.value.slice(0, start)}${replacement}${el.value.slice(end)}`;
  // Highlight the URL portion so the user can immediately type over it.
  const urlStart = start + selected.length + 3; // `[selected](`
  return {
    value: next,
    selection: { start: urlStart, end: urlStart + placeholderUrl.length },
  };
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 18,
  id,
  describedBy,
  className,
}: MarkdownEditorProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [mode, setMode] = React.useState<ToolbarMode>('edit');

  const apply = React.useCallback(
    (
      fn: (el: HTMLTextAreaElement) => {
        value: string;
        selection: { start: number; end: number };
      },
    ) => {
      const el = textareaRef.current;
      if (!el || disabled) return;
      const { value: next, selection } = fn(el);
      onChange(next);
      // Restore caret / selection after React re-renders.
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(selection.start, selection.end);
      });
    },
    [onChange, disabled],
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabled) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        apply((el) => applyInline(el, '**', '**'));
      } else if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        apply((el) => applyInline(el, '_', '_'));
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        apply((el) => applyLink(el));
      }
    },
    [apply, disabled],
  );

  return (
    <div className={cn('rounded border border-border bg-background', className)}>
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/30 px-1 py-1"
        role="toolbar"
        aria-label="Markdown formatting"
      >
        <ToolbarButton
          aria="Bold (Cmd+B)"
          onClick={() => apply((el) => applyInline(el, '**', '**'))}
          disabled={disabled || mode === 'preview'}
        >
          <Bold className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          aria="Italic (Cmd+I)"
          onClick={() => apply((el) => applyInline(el, '_', '_'))}
          disabled={disabled || mode === 'preview'}
        >
          <Italic className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarSeparator />
        <ToolbarButton
          aria="Heading 1"
          onClick={() => apply((el) => applyLinePrefix(el, '# '))}
          disabled={disabled || mode === 'preview'}
        >
          <Heading1 className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          aria="Heading 2"
          onClick={() => apply((el) => applyLinePrefix(el, '## '))}
          disabled={disabled || mode === 'preview'}
        >
          <Heading2 className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          aria="Heading 3"
          onClick={() => apply((el) => applyLinePrefix(el, '### '))}
          disabled={disabled || mode === 'preview'}
        >
          <Heading3 className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarSeparator />
        <ToolbarButton
          aria="Bulleted list"
          onClick={() => apply((el) => applyLinePrefix(el, '- '))}
          disabled={disabled || mode === 'preview'}
        >
          <List className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          aria="Numbered list"
          onClick={() => apply((el) => applyLinePrefix(el, '1. '))}
          disabled={disabled || mode === 'preview'}
        >
          <ListOrdered className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          aria="Blockquote"
          onClick={() => apply((el) => applyLinePrefix(el, '> '))}
          disabled={disabled || mode === 'preview'}
        >
          <Quote className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarSeparator />
        <ToolbarButton
          aria="Code"
          onClick={() => apply((el) => applyInline(el, '`', '`'))}
          disabled={disabled || mode === 'preview'}
        >
          <Code className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          aria="Insert link (Cmd+K)"
          onClick={() => apply((el) => applyLink(el))}
          disabled={disabled || mode === 'preview'}
        >
          <LinkIcon className="h-3.5 w-3.5" aria-hidden />
        </ToolbarButton>
        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarToggleButton
            aria="Editor view"
            active={mode === 'edit'}
            onClick={() => setMode('edit')}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </ToolbarToggleButton>
          <ToolbarToggleButton
            aria="Preview"
            active={mode === 'preview'}
            onClick={() => setMode('preview')}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
          </ToolbarToggleButton>
        </div>
      </div>

      {mode === 'edit' ? (
        <textarea
          ref={textareaRef}
          id={id}
          aria-describedby={describedBy}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={cn(
            'block w-full resize-y rounded-b bg-background px-3 py-2 font-mono text-[12px] leading-relaxed text-foreground',
            'placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
      ) : (
        <div className="prose prose-sm max-w-none px-4 py-3 text-[13px] leading-relaxed dark:prose-invert">
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-muted-foreground">Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

interface ToolbarBtnProps extends React.PropsWithChildren {
  aria: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ aria, onClick, disabled, children }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={aria}
      title={aria}
      disabled={disabled}
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors',
        'hover:bg-foreground/5 hover:text-foreground',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
      )}
    >
      {children}
    </button>
  );
}

function ToolbarToggleButton({
  aria,
  onClick,
  active,
  children,
}: ToolbarBtnProps & { active: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={aria}
      aria-pressed={active}
      title={aria}
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded transition-colors',
        active
          ? 'bg-foreground/5 text-foreground'
          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function ToolbarSeparator() {
  return <span aria-hidden className="mx-1 h-3 w-px bg-border" />;
}

// ────────────────────────────────────────────────────────────────────────
// Test surfaces — exported intentionally for the Vitest unit tests so we
// don't reach into the rendered DOM to verify the transforms.
// ────────────────────────────────────────────────────────────────────────

/** @internal — exposed for unit tests. */
export const __testing__ = {
  applyInline,
  applyLinePrefix,
  applyLink,
};
